import { isAfterBookingToday } from "@shyne/shared";
import moment from "moment";
import { api } from "@/lib/api";
import { bookedAppointmentKeys, duplicateKey } from "./bulk-upload-duplicates";
import { everyProperty } from "@/repositories/property-records";
import { everyUnit, inBatches, mapWithConcurrency } from "@/repositories/unit-records";
import { SERVICE_NAME_MAX_LENGTH, SERVICE_NAMES_PER_LIST_MAX, UNIT_NUMBERS_PER_LIST_MAX } from "@shyne/shared/api";
import { CatalogRepository } from "@/repositories/catalog.repository";
import { everyVendorOf } from "@/repositories/vendors.repository";

export type BulkUploadPlanMode = { kind: "pmc"; clientOrgId: string } | { kind: "vendor"; vendorOrgId: string };

export interface BulkUploadInputRow {
  rowNumber: number;
  property: string;
  property_unit: string;
  service: string;
  sub_service: string;
  vendor?: string;
  date: string;
  work_notes?: string;
}

export interface PlannedAppointmentRow {
  rowNumber: number;
  clientOrgId: string;
  vendorOrgId: string;
  vendorName: string;
  propertyId: string;
  propertyName: string;
  unitId: string | null;
  unitNumber: string;
  serviceId: string;
  serviceName: string;
  appointmentDate: string;
  workNotes: string | null;
}

export interface BulkUploadPlan {
  ready: PlannedAppointmentRow[];
  missingUnits: PlannedAppointmentRow[];
  duplicates: string[];
  errors: string[];
}

type Candidate = { error: string } | { planned: PlannedAppointmentRow };

interface PlanLookups {
  properties: Map<string, { id: string; organization: string }[]>;
  vendors: Map<string, { id: string; display_name: string }[]>;
  vendorOrg: { id: string; display_name: string } | null;
  services: Map<string, string[]>;
  units: Map<string, string[]>;
}

const unique = (values: string[]) => [...new Set(values)];

const LOOKUPS_AT_ONCE = 4;

const actingOrganizationOf = (mode: BulkUploadPlanMode) => (mode.kind === "pmc" ? mode.clientOrgId : mode.vendorOrgId);

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return groups;
}

async function fetchPropertyMatches(
  names: string[],
  mode: BulkUploadPlanMode
): Promise<Map<string, { id: string; organization: string }[]>> {
  const scope = mode.kind === "pmc" ? { customer: mode.clientOrgId } : { vendor: mode.vendorOrgId };
  const wanted = new Set(names);
  try {
    const properties = await everyProperty(api.properties.list, { actingOrganization: actingOrganizationOf(mode), ...scope });
    const named = properties.filter((property) => wanted.has(property.name));
    return groupBy(
      named.map((property) => ({ id: property.id, name: property.name, organization: property.customer?.id ?? "" })),
      (property) => property.name
    );
  } catch (error) {
    throw new Error(`Failed to look up properties: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function fetchVendorMatches(customer: string, names: string[]): Promise<Map<string, { id: string; display_name: string }[]>> {
  const wanted = new Set(names.filter(Boolean));
  if (wanted.size === 0) return new Map();
  try {
    const vendors = (await everyVendorOf(customer)).filter((vendor) => wanted.has(vendor.name));
    return groupBy(
      vendors.map((vendor) => ({ id: vendor.id, display_name: vendor.name })),
      (vendor) => vendor.display_name
    );
  } catch (error) {
    throw new Error(`Failed to look up vendors: ${error instanceof Error ? error.message : String(error)}`);
  }
}

async function fetchVendorOrg(vendorOrgId: string): Promise<{ id: string; display_name: string }> {
  try {
    const { id, name } = await api.organizations.settings(vendorOrgId);
    return { id, display_name: name };
  } catch {
    throw new Error("Failed to load your organization");
  }
}

const serviceKey = (vendor: string, name: string) => `${vendor}|${name.trim().toLowerCase()}`;

const SERVICE_NAMES_QUERY_BYTES = 6 * 1024;

async function fetchServiceMatches(mode: BulkUploadPlanMode, names: string[]): Promise<Map<string, string[]>> {
  const wanted = unique(names.map((name) => name.trim()).filter((name) => name.length > 0 && name.length <= SERVICE_NAME_MAX_LENGTH && !name.includes("\0")));
  if (wanted.length === 0) return new Map();
  const batches = inBatches(wanted, {
    count: SERVICE_NAMES_PER_LIST_MAX,
    bytes: SERVICE_NAMES_QUERY_BYTES,
    sizeOf: (name) => encodeURIComponent(name).length + "&name=".length,
  });
  try {
    const found = await mapWithConcurrency(batches, LOOKUPS_AT_ONCE, (batch) =>
      CatalogRepository.every({
        actingOrganization: actingOrganizationOf(mode),
        ...(mode.kind === "vendor" ? { vendor: [mode.vendorOrgId] } : {}),
        name: batch,
      })
    );
    const grouped = groupBy(found.flat(), (service) => serviceKey(service.vendor.id, service.name));
    return new Map([...grouped].map(([key, services]) => [key, services.map((service) => service.id)]));
  } catch (error) {
    throw new Error(`Failed to look up services: ${error instanceof Error ? error.message : String(error)}`);
  }
}

const UNIT_NUMBERS_QUERY_BYTES = 6 * 1024;

function unitNumbersPerProperty(rows: BulkUploadInputRow[], properties: Map<string, { id: string }[]>): Map<string, string[]> {
  const wanted = new Map<string, Set<string>>();
  for (const row of rows) {
    for (const property of properties.get(row.property) ?? []) {
      const numbers = wanted.get(property.id) ?? new Set<string>();
      numbers.add(row.property_unit);
      wanted.set(property.id, numbers);
    }
  }
  return new Map([...wanted].map(([property, numbers]) => [property, [...numbers]]));
}

async function fetchUnitMatches(wanted: Map<string, string[]>, mode: BulkUploadPlanMode): Promise<Map<string, string[]>> {
  const lookups = [...wanted].flatMap(([property, unitNumbers]) =>
    inBatches(unitNumbers, {
      count: UNIT_NUMBERS_PER_LIST_MAX,
      bytes: UNIT_NUMBERS_QUERY_BYTES,
      sizeOf: (number) => encodeURIComponent(number).length + "&numbers=".length,
    }).map((numbers) => ({ property, numbers }))
  );
  if (lookups.length === 0) return new Map();
  try {
    const found = await mapWithConcurrency(lookups, LOOKUPS_AT_ONCE, ({ property, numbers }) =>
      everyUnit(api.units.list, property, { actingOrganization: actingOrganizationOf(mode), numbers })
    );
    const grouped = groupBy(found.flat(), (unit) => `${unit.property}|${unit.number}`);
    return new Map([...grouped].map(([key, units]) => [key, units.map((unit) => unit.id)]));
  } catch (error) {
    throw new Error(`Failed to look up property units: ${error instanceof Error ? error.message : String(error)}`);
  }
}

function resolveUnique<T>(matches: T[] | undefined, missing: string, ambiguous: string): { value: T } | { error: string } {
  if (!matches || matches.length === 0) return { error: missing };
  if (matches.length > 1) return { error: ambiguous };
  return { value: matches[0] };
}

function resolveRow(row: BulkUploadInputRow, mode: BulkUploadPlanMode, lookups: PlanLookups): Candidate {
  const property = resolveUnique(
    lookups.properties.get(row.property),
    `Row ${row.rowNumber}: Property "${row.property}" not found`,
    `Row ${row.rowNumber}: Property "${row.property}" matches multiple properties`
  );
  if ("error" in property) return property;

  const vendor =
    mode.kind === "vendor"
      ? { value: lookups.vendorOrg! }
      : resolveUnique(
          lookups.vendors.get(row.vendor ?? ""),
          `Row ${row.rowNumber}: Vendor "${row.vendor}" not found`,
          `Row ${row.rowNumber}: Vendor "${row.vendor}" matches multiple organizations`
        );
  if ("error" in vendor) return vendor;

  const service = resolveUnique(
    lookups.services.get(serviceKey(vendor.value.id, row.sub_service)),
    `Row ${row.rowNumber}: Service "${row.sub_service}" not found for vendor "${vendor.value.display_name}"`,
    `Row ${row.rowNumber}: Service "${row.sub_service}" matches multiple services for vendor "${vendor.value.display_name}"`
  );
  if ("error" in service) return service;

  const date = moment(row.date);
  if (!date.isValid()) {
    return { error: `Row ${row.rowNumber}: Invalid date format "${row.date}"` };
  }
  const day = date.format("YYYY-MM-DD");
  if (mode.kind === "pmc" && !isAfterBookingToday(day)) {
    return { error: `Row ${row.rowNumber}: Pick a day after today.` };
  }

  const unitMatches = lookups.units.get(`${property.value.id}|${row.property_unit}`) ?? [];
  if (unitMatches.length > 1) {
    return {
      error: `Row ${row.rowNumber}: Unit "${row.property_unit}" matches multiple units in property "${row.property}"`,
    };
  }

  return {
    planned: {
      rowNumber: row.rowNumber,
      clientOrgId: mode.kind === "pmc" ? mode.clientOrgId : property.value.organization,
      vendorOrgId: vendor.value.id,
      vendorName: vendor.value.display_name,
      propertyId: property.value.id,
      propertyName: row.property,
      unitId: unitMatches[0] ?? null,
      unitNumber: row.property_unit,
      serviceId: service.value,
      serviceName: row.sub_service,
      appointmentDate: day,
      workNotes: row.work_notes || null,
    },
  };
}

async function fetchLookups(rows: BulkUploadInputRow[], mode: BulkUploadPlanMode): Promise<PlanLookups> {
  const [properties, vendors, vendorOrg] = await Promise.all([
    fetchPropertyMatches(unique(rows.map((row) => row.property)), mode),
    mode.kind === "pmc"
      ? fetchVendorMatches(mode.clientOrgId, unique(rows.map((row) => row.vendor ?? "")))
      : Promise.resolve(new Map<string, { id: string; display_name: string }[]>()),
    mode.kind === "vendor" ? fetchVendorOrg(mode.vendorOrgId) : Promise.resolve(null),
  ]);

  const [services, units] = await Promise.all([
    fetchServiceMatches(mode, rows.map((row) => row.sub_service)),
    fetchUnitMatches(unitNumbersPerProperty(rows, properties), mode),
  ]);

  return { properties, vendors, vendorOrg, services, units };
}

export async function planAppointmentBulkUpload(
  rows: BulkUploadInputRow[],
  mode: BulkUploadPlanMode
): Promise<BulkUploadPlan> {
  const ready: PlannedAppointmentRow[] = [];
  const missingUnits: PlannedAppointmentRow[] = [];
  const duplicates: string[] = [];
  const errors: string[] = [];

  if (rows.length === 0) {
    return { ready, missingUnits, duplicates, errors };
  }

  const lookups = await fetchLookups(rows, mode);
  const candidates = rows.map((row) => resolveRow(row, mode, lookups));
  const plannedCandidates = candidates.flatMap((candidate) => ("planned" in candidate ? [candidate.planned] : []));
  const seenKeys = await bookedAppointmentKeys(api.appointments.list, plannedCandidates, actingOrganizationOf(mode));

  for (const candidate of candidates) {
    if ("error" in candidate) {
      errors.push(candidate.error);
      continue;
    }

    const { planned } = candidate;
    const key = duplicateKey(planned);
    if (seenKeys.has(key)) {
      duplicates.push(
        `Row ${planned.rowNumber}: ${planned.propertyName} unit ${planned.unitNumber} — "${planned.serviceName}" on ${planned.appointmentDate} already exists`
      );
      continue;
    }

    seenKeys.add(key);
    if (planned.unitId) {
      ready.push(planned);
    } else {
      missingUnits.push(planned);
    }
  }

  return { ready, missingUnits, duplicates, errors };
}
